import { StatusTag } from "@graffer-inc/console-design-kit";
import { ComponentProps } from "react";
import { ProductCodeTypeLiterals } from "../organization/org-product";

type ValueOf<T> = T[keyof T];

/*
 * ENUMs
 */
export const TargetType = {
  Application: "Application",
  SurveyAnswer: "SurveyAnswer",
} as const;

export const Status = {
  WaitingApproval: "WaitingApproval",
  Scheduled: "Scheduled",
  WIP: "WIP",
  Done: "Done",
  Canceled: "Canceled",
  Error: "Error",
} as const;

/*
 * 製品データ削除タスク
 *
 * バックエンドの型は以下を参照
 * backend/src/presentation/rest/product_data_removal_task/response.go
 */
export type ProductDataRemovalTask = {
  id: string;
  orgId: string;
  targetType: ValueOf<typeof TargetType>;
  status: ValueOf<typeof Status>;
  dueAt: Date;
  targetCount: number;
  processStartedAt: Date | null;
  processEndedAt: Date | null;
  approvedAt: Date | null;
  approvedBy: string | null;
  canceledAt: Date | null;
  canceledBy: string | null;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  updatedBy: string;
};

export type ProductDataRemovalTaskResponse = {
  id: string;
  orgId: string;
  targetType: ValueOf<typeof TargetType>;
  status: ValueOf<typeof Status>;
  dueAt: string;
  targetCount: number;
  processStartedAt: string | null;
  processEndedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  canceledAt: string | null;
  canceledBy: string | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export type ProductDataRemovalTaskCreateRequest = {
  dueAt: string;
};

export const toProductDataRemovalTask = (
  response: ProductDataRemovalTaskResponse,
): ProductDataRemovalTask => ({
  ...response,
  dueAt: new Date(response.dueAt),
  processStartedAt: response.processStartedAt
    ? new Date(response.processStartedAt)
    : null,
  processEndedAt: response.processEndedAt
    ? new Date(response.processEndedAt)
    : null,
  approvedAt: response.approvedAt ? new Date(response.approvedAt) : null,
  canceledAt: response.canceledAt ? new Date(response.canceledAt) : null,
  createdAt: new Date(response.createdAt),
  updatedAt: new Date(response.updatedAt),
});

export const toProductDataRemovalTaskRequest = (
  body: Pick<ProductDataRemovalTask, "dueAt">,
): ProductDataRemovalTaskCreateRequest => ({
  dueAt: body.dueAt.toISOString(),
});

/*
 * Statusに関する定義
 */
export const ApprovableStatuses: ProductDataRemovalTask["status"][] = [
  Status.WaitingApproval,
] as const satisfies ProductDataRemovalTask["status"][];
export const CancelableStatuses: ProductDataRemovalTask["status"][] = [
  Status.WaitingApproval,
  Status.Scheduled,
] as const satisfies ProductDataRemovalTask["status"][];
export const NonTerminalStatuses: ProductDataRemovalTask["status"][] = [
  Status.WaitingApproval,
  Status.Scheduled,
  Status.WIP,
  Status.Error,
] as const satisfies ProductDataRemovalTask["status"][];

export const StatusLabel = {
  WaitingApproval: "承認待ち",
  Scheduled: "削除予定",
  WIP: "削除中",
  Done: "削除完了",
  Canceled: "キャンセル",
  Error: "エラー",
} as const satisfies { [key in ProductDataRemovalTask["status"]]: string };

type GcdkColorType = ComponentProps<typeof StatusTag>["type"];
export const StatusTagColor = {
  WaitingApproval: "PRIMARY",
  Scheduled: "LIGHT",
  WIP: "LIGHT",
  Done: "SUCCESS",
  Canceled: "SECONDARY",
  Error: "ERROR",
} as const satisfies {
  [key in ProductDataRemovalTask["status"]]: GcdkColorType;
};

/*
 * TargetTypeに関する定義
 */
const ProductDataRemovableProductCodes = [
  "SmartApply",
  "Survey",
] as const satisfies ProductCodeTypeLiterals[];

export type ProductDataRemovableProductCodeLiterals =
  (typeof ProductDataRemovableProductCodes)[number];

export const ProductCodeTargetTypeMap = {
  SmartApply: TargetType.Application,
  Survey: TargetType.SurveyAnswer,
} as const satisfies {
  [key in ProductDataRemovableProductCodeLiterals]: ProductDataRemovalTask["targetType"];
};

export const TargetTypeApiPathParamMap = {
  [TargetType.Application]: "applications",
  [TargetType.SurveyAnswer]: "survey-answers",
} as const satisfies {
  [key in ProductDataRemovalTask["targetType"]]: string;
};
